import React, { useMemo } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import format from 'date-fns/format';
import { useTranslation } from 'react-i18next';

import { getTranslation } from 'helpers/getTranslation';

import { Loading } from 'components/shared/Loading';
import { Select } from 'components/shared/Select';
import { Typography } from 'components/shared/Typography';
import { Tabs } from 'components/shared/Tabs';
import { Button } from 'components/shared/Button';
import { TablePagination } from 'components/TablePagination';

import { MenuLayout } from 'components/Layout';
import { ListToolbar } from 'components/Toolbar';

import {
  buildFunnelChartData,
  buildMultiTimeSeriesChartData,
  buildRetentionChartData,
  buildTimeSeriesChartData,
  getAllEventsOption,
  getFunnelPresetOptions,
  getPeriodOptions,
  getPlatformOptions,
  type AnalyticsChartType,
} from './AnalyticsPage.helpers';

import styles from './AnalyticsPage.module.scss';

const AnalyticsPage = ({
  loading,
  presets,
  activePresetId,
  chartType,
  isCombinedPayments,
  onPresetSelect,
  period,
  onPeriodChange,
  platform,
  onPlatformChange,
  eventName,
  onEventNameChange,
  eventNames,
  funnelPreset,
  onFunnelPresetChange,
  timeSeriesSeries,
  funnelData,
  retentionData,
  events,
  eventsPage,
  eventsTotalPages,
  onEventsNextPage,
  onEventsPrevPage,
  dashboards,
  onSaveDashboard,
  onDeleteDashboard,
  onRefresh,
}: {
  loading: boolean;
  presets: Array<{
    id: string;
    name: string;
    description: string;
    config: Record<string, unknown>;
  }>;
  activePresetId: string;
  chartType: AnalyticsChartType;
  isCombinedPayments: boolean;
  onPresetSelect: (preset: {
    id: string;
    name: string;
    description: string;
    config: Record<string, unknown>;
  }) => void;
  period: { label: string; value: string };
  onPeriodChange: (option: { label: string; value: string }) => void;
  platform: { label: string; value: string };
  onPlatformChange: (option: { label: string; value: string }) => void;
  eventName: { label: string; value: string };
  onEventNameChange: (option: { label: string; value: string }) => void;
  eventNames: string[];
  funnelPreset: { label: string; value: string };
  onFunnelPresetChange: (option: { label: string; value: string }) => void;
  timeSeriesSeries: Array<{
    id: string;
    label: string;
    color: string;
    data: Array<{ date: string; count: number }>;
  }>;
  funnelData: Array<{ step: string; count: number; conversionRate: number }>;
  retentionData: {
    cohortSize: number;
    data: Array<{ day: number; retained: number; rate: number }>;
  };
  events: Array<{
    _id: string;
    eventName: string;
    platform: string;
    studentId?: string | null;
    timestamp: string;
    properties?: Record<string, unknown>;
  }>;
  eventsPage: number;
  eventsTotalPages: number;
  onEventsNextPage: () => void;
  onEventsPrevPage: () => void;
  dashboards: Array<{ _id: string; name: string; description?: string | null }>;
  onSaveDashboard: () => void;
  onDeleteDashboard: (dashboardId: string) => void;
  onRefresh: () => void;
}) => {
  const { i18n } = useTranslation();

  const periodOptions = useMemo(() => getPeriodOptions(), [i18n.language]);
  const platformOptions = useMemo(() => getPlatformOptions(), [i18n.language]);
  const funnelPresetOptions = useMemo(() => getFunnelPresetOptions(), [i18n.language]);
  const allEventsOption = useMemo(() => getAllEventsOption(), [i18n.language]);

  const eventNameOptions = [
    allEventsOption,
    ...eventNames.map((name) => ({ label: name, value: name })),
  ];

  const lineData = isCombinedPayments
    ? buildMultiTimeSeriesChartData(timeSeriesSeries)
    : buildTimeSeriesChartData(
        timeSeriesSeries[0]?.data ?? [],
        timeSeriesSeries[0]?.label ?? 'events',
        timeSeriesSeries[0]?.color ?? '#85E0AB',
      );
  const lineColors = isCombinedPayments
    ? timeSeriesSeries.map((series) => series.color)
    : [timeSeriesSeries[0]?.color ?? '#85E0AB'];
  const funnelChartData = buildFunnelChartData(funnelData);
  const retentionChartData = buildRetentionChartData(retentionData.data ?? []);

  const overviewLabel = getTranslation('analytics_page.tabs.overview');
  const explorerLabel = getTranslation('analytics_page.tabs.explorer');
  const dashboardsLabel = getTranslation('analytics_page.tabs.dashboards');

  const overviewContent = loading ? (
    <Loading className={styles.loading} />
  ) : (
    <>
      <div className={styles.presetGrid}>
        {presets.map((preset) => (
          <button
            key={preset.id}
            type="button"
            className={
              preset.id === activePresetId
                ? `${styles.presetCard} ${styles.presetCardActive}`
                : styles.presetCard
            }
            onClick={() => onPresetSelect(preset)}
          >
            <Typography variant="body1" weight="medium">
              {preset.name}
            </Typography>
            <Typography variant="body2" mode="compact" className={styles.presetDescription}>
              {preset.description}
            </Typography>
          </button>
        ))}
      </div>

      <div className={styles.section}>
        {chartType === 'time_series' && (
          <div className={`${styles.card} ${isCombinedPayments ? styles.sectionFull : ''}`}>
            <div className={styles.cardHeader}>
              <Typography variant="heading3" weight="semiBold">
                {isCombinedPayments
                  ? getTranslation('analytics_page.charts.payments')
                  : getTranslation('analytics_page.charts.events_over_time')}
              </Typography>
              {isCombinedPayments && (
                <Typography variant="body2" mode="compact">
                  {getTranslation('analytics_page.charts.payments_sources')}
                </Typography>
              )}
            </div>
            <div className={styles.chart}>
              <ResponsiveLine
                data={lineData}
                margin={{ top: 16, right: 24, bottom: 40, left: 48 }}
                xScale={{ type: 'point' }}
                yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
                axisBottom={{ tickRotation: -35 }}
                colors={lineColors}
                pointSize={6}
                useMesh
                enableSlices="x"
                legends={
                  isCombinedPayments
                    ? [
                        {
                          anchor: 'bottom-right',
                          direction: 'column',
                          translateX: 120,
                          itemWidth: 180,
                          itemHeight: 20,
                          symbolSize: 12,
                          symbolShape: 'circle',
                        },
                      ]
                    : undefined
                }
              />
            </div>
          </div>
        )}

        {chartType === 'funnel' && (
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <Typography variant="heading3" weight="semiBold">
                {getTranslation('analytics_page.charts.funnel')}
              </Typography>
            </div>
            <div className={styles.chart}>
              <ResponsiveBar
                data={funnelChartData}
                keys={['count']}
                indexBy="step"
                margin={{ top: 16, right: 24, bottom: 72, left: 48 }}
                padding={0.3}
                colors={['#85E0AB']}
                axisBottom={{ tickRotation: -25 }}
                labelSkipWidth={12}
                labelSkipHeight={12}
              />
            </div>
          </div>
        )}

        {chartType === 'retention' && (
          <div className={`${styles.card} ${styles.sectionFull}`}>
            <div className={styles.cardHeader}>
              <Typography variant="heading3" weight="semiBold">
                {getTranslation('analytics_page.charts.retention')}
              </Typography>
              <Typography variant="body2" mode="compact">
                {getTranslation('analytics_page.charts.cohort_size', {
                  value: retentionData.cohortSize ?? 0,
                })}
              </Typography>
            </div>
            <div className={styles.chart}>
              <ResponsiveBar
                data={retentionChartData}
                keys={['rate']}
                indexBy="day"
                margin={{ top: 16, right: 24, bottom: 40, left: 48 }}
                padding={0.4}
                colors={['#5C738C']}
                labelFormat={(value) => `${value}%`}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );

  const explorerContent = loading ? (
    <Loading className={styles.loading} />
  ) : (
    <div className={`${styles.card} ${styles.sectionFull}`}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>{getTranslation('analytics_page.table.event')}</th>
            <th>{getTranslation('analytics_page.table.platform')}</th>
            <th>{getTranslation('analytics_page.table.student')}</th>
            <th>{getTranslation('analytics_page.table.time')}</th>
            <th>{getTranslation('analytics_page.table.properties')}</th>
          </tr>
        </thead>
        <tbody>
          {events.map((event) => (
            <tr key={event._id}>
              <td>{event.eventName}</td>
              <td>{event.platform}</td>
              <td>{event.studentId ?? '—'}</td>
              <td>{format(new Date(event.timestamp), 'MMM dd, yyyy HH:mm')}</td>
              <td>
                <div className={styles.properties}>
                  {JSON.stringify(event.properties ?? {}, null, 2)}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <TablePagination
        currentPage={eventsPage}
        perPage={20}
        totalPages={eventsTotalPages}
        onNextPageClick={onEventsNextPage}
        onPrevPageClick={onEventsPrevPage}
        onPerPageChange={(): void => undefined}
      />
    </div>
  );

  const dashboardsContent = loading ? (
    <Loading className={styles.loading} />
  ) : (
    <div className={`${styles.card} ${styles.sectionFull}`}>
      <div className={styles.cardHeader}>
        <Typography variant="heading3" weight="semiBold">
          {getTranslation('analytics_page.dashboards.title')}
        </Typography>
        <Button size="md" onClick={onSaveDashboard}>
          {getTranslation('analytics_page.dashboards.save_current')}
        </Button>
      </div>
      <div className={styles.dashboardList}>
        {dashboards.length === 0 ? (
          <Typography variant="body2">
            {getTranslation('analytics_page.dashboards.empty')}
          </Typography>
        ) : (
          dashboards.map((dashboard) => (
            <div key={dashboard._id} className={styles.dashboardItem}>
              <div>
                <Typography variant="body1" weight="medium">
                  {dashboard.name}
                </Typography>
                {dashboard.description && (
                  <Typography variant="body2" mode="compact">
                    {dashboard.description}
                  </Typography>
                )}
              </div>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => onDeleteDashboard(dashboard._id)}
              >
                {getTranslation('common.button.delete')}
              </Button>
            </div>
          ))
        )}
      </div>
    </div>
  );

  return (
    <MenuLayout>
      <div className={styles.AnalyticsPage}>
        <div className={styles.header}>
          <ListToolbar title={getTranslation('analytics_page.title')} />
          <Button size="md" variant="secondary" onClick={onRefresh}>
            {getTranslation('analytics_page.refresh')}
          </Button>
        </div>

        <div className={styles.filters}>
          <Select options={periodOptions} value={period} onChange={onPeriodChange} />
          <Select
            options={platformOptions}
            value={platform}
            onChange={onPlatformChange}
          />
          <Select
            options={eventNameOptions}
            value={eventName}
            onChange={onEventNameChange}
          />
          {chartType === 'funnel' && (
            <Select
              options={funnelPresetOptions}
              value={funnelPreset}
              onChange={onFunnelPresetChange}
            />
          )}
        </div>

        <Tabs
          className={styles.tabs}
          items={[
            { label: overviewLabel, content: overviewContent },
            { label: explorerLabel, content: explorerContent },
            { label: dashboardsLabel, content: dashboardsContent },
          ]}
        />
      </div>
    </MenuLayout>
  );
};

export default React.memo(AnalyticsPage) as any;
